import { PubSub } from '@aws-amplify/pubsub';
import { fetchAuthSession } from 'aws-amplify/auth';
import { post } from 'aws-amplify/api';
import { useCallback, useEffect, useRef } from 'react';
import { REGION } from 'src/models/globals';

/**
 * Determines whether to use WebSocket (true) or IoT PubSub (false) based on region and environment.
 */
const USE_WEBSOCKET = REGION === 'af-south-1' || process.env.REACT_APP_ENV === 'dev';

/**
 * Custom hook that abstracts the transport layer for preview dialing.
 * Handles call status subscriptions, placing dials, and contact actions (approve/recycle/suppress).
 *
 * - IoT PubSub: Used in most regions. Publishes actions to IoT topics and subscribes via MQTT.
 * - WebSocket: Used in af-south-1 and dev. Calls REST APIs directly and subscribes via native WebSocket.
 *
 * @param {Object} params
 * @param {string} params.agentId - The agent's ID.
 * @param {string} params.tenantId - The tenant ID.
 * @param {string} params.apiKey - The API key for REST calls.
 * @param {function} params.onCallStatusChange - Callback when call status changes.
 * @param {function} params.onAllowDial - Callback when allow-dial message is received (IoT/Webex only).
 */
export function useDialTransport({ agentId, tenantId, apiKey, onCallStatusChange, onAllowDial }) {
	const callStatusSubscription = useRef(null);
	const allowDialSubscription = useRef(null);

	const pubsubRef = useRef(
		!USE_WEBSOCKET
			? new PubSub({
				region: REGION,
				endpoint: `wss://a3b8mw0zsvy66t-ats.iot.${REGION}.amazonaws.com/mqtt`,
			})
			: null,
	);

	/**
	 * Subscribes to call status updates.
	 */
	const subscribeToCallStatus = useCallback(() => {
		if (USE_WEBSOCKET) {
			subscribeToCallStatusWS();
		} else {
			subscribeToCallStatusIoT();
		}
	}, [agentId, tenantId]);

	function subscribeToCallStatusIoT() {
		try {
			callStatusSubscription.current?.unsubscribe();
			callStatusSubscription.current = pubsubRef.current
				.subscribe({
					topics: `approvals/${process.env.REACT_APP_ENV}/${tenantId}/agent-call-statuses/${agentId}`,
				})
				.subscribe({
					next: (message) => onCallStatusChange?.(message),
					error: (error) => console.error('Error subscribing to call status (IoT)', error),
					complete: () => console.log('Call Status IoT subscription complete'),
				});
		} catch (error) {
			console.error('Error subscribing to call status (IoT)', error);
		}
	}

	function subscribeToCallStatusWS(retryCount = 0) {
		try {
			callStatusSubscription.current?.close?.();

			const wsUrl = `wss://${process.env.REACT_APP_ENV}.ws.customerdynamics.com?agentId=${agentId}`;
			const ws = new WebSocket(wsUrl);
			callStatusSubscription.current = ws;

			let hasErrored = false;

			ws.onopen = () => console.log('Call Status WebSocket connection established');

			ws.onmessage = (event) => {
				try {
					const message = JSON.parse(event.data);
					onCallStatusChange?.(message);
				} catch (err) {
					console.error('Error parsing call status message', err);
				}
			};

			ws.onerror = (error) => {
				console.error('WebSocket error', error);
				hasErrored = true;
			};

			ws.onclose = () => {
				console.log('Call Status WebSocket connection closed');
				if (hasErrored && retryCount < 5) {
					const delay = 2000 * (retryCount + 1);
					console.log(`Reconnecting WebSocket... attempt ${retryCount + 1}`);
					setTimeout(() => subscribeToCallStatusWS(retryCount + 1), delay);
				} else if (retryCount >= 5) {
					console.error('Max WebSocket retry attempts reached');
				}
			};
		} catch (error) {
			console.error('Error subscribing to call status (WebSocket)', error);
		}
	}

	/**
	 * Subscribes to the allow-dial topic (IoT only, used for Webex).
	 */
	const subscribeToAllowDial = useCallback(() => {
		if (USE_WEBSOCKET) return; // Not needed for WebSocket transport
		allowDialSubscription.current?.unsubscribe();
		const baseTopic = `approvals/${process.env.REACT_APP_ENV}/${tenantId}`;
		allowDialSubscription.current = pubsubRef.current
			.subscribe({
				topics: `${baseTopic}/c2c/${agentId}`,
			})
			.subscribe({
				next: (message) => onAllowDial?.(message),
				error: (error) => console.error('Error in agent allow dial subscription', error),
				complete: () => console.log('Allow dial subscription closed.'),
			});
	}, [agentId, tenantId, onAllowDial]);

	/**
	 * Places a dial or performs a contact action (approve/recycle/suppress).
	 *
	 * IoT: publishes the action message to the campaign topic.
	 * WebSocket: calls the REST API directly for 'approve', or the disposition API for 'suppress'.
	 *
	 * @param {Object} params
	 * @param {string} params.type - 'approve', 'recycle', or 'suppress'
	 * @param {Object} params.contactHash - The contact hash object.
	 * @param {Object} params.activeContact - The full contact object.
	 * @param {string} params.phoneNumber - The phone number to dial.
	 * @param {Object} params.campaign - The selected campaign.
	 * @param {string} params.suppressionProfileId - The suppression profile ID.
	 */
	const performContactAction = useCallback(async ({ type, contactHash, activeContact, phoneNumber, campaign, suppressionProfileId }) => {
		if (USE_WEBSOCKET) {
			return performContactActionWS({ type, contactHash, activeContact, phoneNumber, campaign, suppressionProfileId });
		} else {
			return performContactActionIoT({ type, contactHash, phoneNumber, campaign });
		}
	}, [tenantId, agentId, apiKey]);

	async function performContactActionIoT({ type, contactHash, phoneNumber, campaign }) {
		contactHash.phone = phoneNumber;
		await pubsubRef.current.publish({
			topics: [
				`approvals/${process.env.REACT_APP_ENV}/${tenantId}/${campaign.id}`,
			],
			message: {
				type,
				skillId: campaign.skillId,
				agent: agentId,
				cci: campaign.cci,
				...contactHash,
				phone: phoneNumber,
				jwtToken: (await fetchAuthSession()).tokens.idToken,
				apiKey,
				preview: true,
			},
		});
	}

	async function performContactActionWS({ type, contactHash, activeContact, phoneNumber, campaign, suppressionProfileId }) {
		const session = await fetchAuthSession();
		const idToken = session.tokens.idToken;

		if (type === 'approve') {
			// Call the /dial endpoint directly
			const dialResult = await post({
				apiName: 'cdyxoutreach',
				path: '/dial',
				options: {
					headers: {
						Authorization: `Bearer ${idToken}`,
						'x-api-key': apiKey,
					},
					body: {
						contact: { ...activeContact, phone: phoneNumber },
						field: 'phone',
						dialingProfileId: campaign.campaignProfileId,
						skillId: campaign.skillId,
						agentId,
					},
				},
			}).response;

			const dialResponse = await dialResult.body.json();
			if (!dialResponse.contactId) {
				throw new Error('Error dialing contact');
			}
		} else if (type === 'suppress') {
			// Call the /disposition endpoint
			await post({
				apiName: 'cdyxoutreach',
				path: '/disposition',
				options: {
					headers: {
						Authorization: `Bearer ${idToken}`,
						'x-api-key': apiKey,
					},
					body: {
						additionalInfo: {
							id: contactHash.id,
							campaignId: contactHash.campaignId,
							segmentId: contactHash.segmentId,
						},
						agent: agentId,
						icUserName: agentId,
						icContactId: '',
						dispClassId: 41,
						cciDispId: 41,
						dispCode: 41,
						dispClassName: 'Contact Suppressed - Preview Dialing',
						dispDescription: 'The contact was suppressed from dialing in preview dialing mode.',
						phoneNumber,
						timeInitiated: new Date().toISOString(),
						timeDisposed: new Date().toISOString(),
						timeDisconnected: new Date().toISOString(),
						id: contactHash.id,
						campaignId: contactHash.campaignId,
						segmentId: contactHash.segmentId,
						crmContactId: contactHash.externalId,
						source: 'Preview Dial',
					},
				},
			});
		}
		// 'recycle' in WS mode is effectively a no-op (contact is just dismissed)
	}

	/**
	 * Checks if a contact is valid for dialing.
	 *
	 * @param {Object} params
	 * @param {Object} params.contact - The contact to check.
	 * @param {string} params.phoneNumber - The phone number being dialed.
	 * @param {string} params.profileId - The campaign profile ID.
	 * @param {string} params.suppressionProfileId - The suppression profile ID.
	 * @returns {Promise<{passed: boolean, message: string}>}
	 */
	const checkContact = useCallback(async ({ contact, phoneNumber, profileId, suppressionProfileId }) => {
		const checkResult = await post({
			apiName: 'cdyxoutreach',
			path: '/dial/check',
			options: {
				headers: {
					Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
					'x-api-key': apiKey,
				},
				body: {
					contact: { ...contact, phone: phoneNumber },
					field: 'phone',
					profileId,
					tenantId,
					suppressionProfileId,
				},
			},
		}).response;

		const checkResponse = await checkResult.body.json();
		const passed = checkResponse?.Message === 'Contact passed check';
		return {
			passed,
			message: passed
				? checkResponse.Message
				: checkResponse?.ErrorMessage || checkResponse?.Message || 'Error checking contact',
		};
	}, [tenantId, apiKey]);

	/**
	 * Cleans up all subscriptions.
	 */
	const cleanup = useCallback(() => {
		if (USE_WEBSOCKET) {
			callStatusSubscription.current?.close?.();
		} else {
			callStatusSubscription.current?.unsubscribe();
			allowDialSubscription.current?.unsubscribe();
		}
	}, []);

	// Auto-cleanup on unmount
	useEffect(() => {
		return () => cleanup();
	}, []);

	return {
		/** Whether the transport is using WebSocket (true) or IoT (false). */
		isWebSocket: USE_WEBSOCKET,
		subscribeToCallStatus,
		subscribeToAllowDial,
		performContactAction,
		checkContact,
		cleanup,
		/** The PubSub instance (null if using WebSocket). Exposed for Webex outdial. */
		pubsub: pubsubRef.current,
	};
}
